import { useState } from "react";
import { Link } from "react-router";
import { Badge, Button, Drawer, InlineAlert, Money, Quantity, RelativeTime } from "@/components";
import { pluralize } from "@/lib/format";
import { useContinuity, type ContinuityEntry } from "../api";
import styles from "./ContinuityPanel.module.css";

const REASON_TEXT: Record<ContinuityEntry["reason"], string> = {
  defunct: "Closed",
  blocked: "Blocks automated access",
  stale: "No fresh catalog data",
  sync_failing: "Catalog sync keeps failing",
};

const RELIANCE_TEXT: Record<string, string> = {
  lot: "bought there",
  confirmed_source: "confirmed source",
  watch: "watched",
  basket: "in a basket",
};

/** A notice when suppliers you rely on closed or went quiet, with alternatives in a side panel. */
export function ContinuityPanel() {
  const continuity = useContinuity();
  const [open, setOpen] = useState(false);
  const entries = continuity.data?.suppliers ?? [];
  if (!entries.length) return null;
  const materials = entries.reduce((sum, entry) => sum + entry.affected.length, 0);
  const names = entries.map((entry) => entry.supplier.name).join(", ");

  return (
    <>
      <InlineAlert
        tone="warning"
        title={`${names} ${entries.length === 1 ? "needs" : "need"} attention`}
        action={<Button onClick={() => setOpen(true)}>Review alternatives</Button>}
      >
        {pluralize(materials, "material you rely on is", "materials you rely on are")} affected. Other suppliers that
        list them are ready to compare.
      </InlineAlert>
      <Drawer open={open} onOpenChange={setOpen} side="end" size="md" title="Supplier continuity">
        <div className={styles.body}>
          {entries.map((entry) => (
            <section key={entry.supplier.id} className={styles.supplier} aria-labelledby={`cont-${entry.supplier.id}`}>
              <header className={styles.header}>
                <h3 id={`cont-${entry.supplier.id}`} className={styles.title}>
                  <Link to={`/suppliers/${entry.supplier.id}`}>{entry.supplier.name}</Link>
                </h3>
                <Badge tone={entry.reason === "defunct" ? "danger" : "warning"} icon="alertTriangle">
                  {REASON_TEXT[entry.reason]}
                </Badge>
              </header>
              <p className={styles.meta}>
                Last good data: <RelativeTime value={entry.last_success_at ?? entry.last_data_at} />
                {entry.last_error ? <span className={styles.error}>{entry.last_error}</span> : null}
              </p>
              <ul className={styles.materials}>
                {entry.affected.map((material) => (
                  <li key={material.material_id} className={styles.material}>
                    <div className={styles.materialHead}>
                      <span className={styles.materialName}>{material.material_name}</span>
                      <span className={styles.reasons}>
                        {material.reasons.map((reason) => RELIANCE_TEXT[reason] ?? reason).join(", ")}
                        {material.owned_g ? (
                          <>
                            {", "}
                            <Quantity value={material.owned_g} unit="g" /> on the shelf
                          </>
                        ) : null}
                      </span>
                    </div>
                    {material.alternatives.length ? (
                      <ul className={styles.alternatives}>
                        {material.alternatives.map((alternative) => (
                          <li key={alternative.variant_id} className={styles.alternative}>
                            <a href={alternative.url} target="_blank" rel="noreferrer" className={styles.link}>
                              {alternative.supplier.name}: {alternative.product_title} {alternative.variant_title}
                            </a>
                            {alternative.via_material ? (
                              <span className={styles.via}>as {alternative.via_material}</span>
                            ) : null}
                            <span className={styles.price}>
                              <Money minor={alternative.price_minor} currency={alternative.currency} />
                              <Money
                                minor={alternative.per_neat_g_minor}
                                currency={alternative.currency}
                                extraDecimals={2}
                                per="g"
                                className={styles.perGram}
                              />
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className={styles.none}>No other supplier lists it yet.</p>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </Drawer>
    </>
  );
}
